import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";

const LeftTable = ({
  tableLeftList,
  loading,
  onAmountChange,
  limitAmount,
  onSelectedRowsChange,
}) => {
  const [selectedToggle, setSelectedToggle] = useState([]);
  const [settledAmounts, setSettledAmounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelectToggle = (index, checked) => {
    setSelectedToggle((prev) => {
      const updated = [...prev];
      updated[index] = checked;
      return updated;
    });

    if (checked) {
      const balAmt = Number(tableLeftList?.[index]?.BalAmt || 0);
      setSettledAmounts((prev) => {
        const updated = [...prev];
        updated[index] = balAmt;
        return updated;
      });
    } else {
      setSettledAmounts((prev) => {
        const updated = [...prev];
        updated[index] = 0;
        return updated;
      });
    }
  };

  const handleSettledChange = (index, value) => {
    const rowAmount = Number(tableLeftList?.[index]?.BalAmt || 0);
    const numericValue = Number(value || 0);

    if (numericValue > rowAmount) {
      alert("Settled amount cannot exceed balance amount");
      return;
    }

    const otherTotal = selectedAmount - (settledAmounts[index] || 0);
    if (otherTotal + numericValue > limitAmount) {
      alert("Amount must not exceed limit!");
      return;
    }

    setSettledAmounts((prev) => {
      const updated = [...prev];
      updated[index] = numericValue;
      return updated;
    });

    setSelectedToggle((prev) => {
      const updated = [...prev];
      updated[index] = numericValue > 0;
      return updated;
    });
  };

  const selectedAmount = tableLeftList?.reduce((total, row, index) => {
    if (selectedToggle[index]) {
      return total + Number(settledAmounts[index] || 0);
    }
    return total;
  }, 0);

  useEffect(() => {
    onAmountChange?.(selectedAmount);
  }, [selectedAmount]);

  const selectedRows = useMemo(() => {
    return tableLeftList
      ?.map((row, index) => {
        if (!selectedToggle[index]) return null;

        return {
          ...row,
          SETTLED_AMOUNT: settledAmounts[index] || 0,
          INDEX: index,
        };
      })
      .filter(Boolean);
  }, [tableLeftList, selectedToggle, settledAmounts]);

  useEffect(() => {
    onSelectedRowsChange?.(selectedRows);
  }, [selectedRows, onSelectedRowsChange]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return tableLeftList?.map((row, i) => ({ ...row, _origIndex: i })) || [];
    const q = searchQuery.toLowerCase();
    return (tableLeftList || [])
      .map((row, i) => ({ ...row, _origIndex: i }))
      .filter((row) => {
        const refNo = (row.INV || "").toLowerCase();
        const balAmt = String(row.BalAmt || "").toLowerCase();
        const desc = (row.DESCR || "").toLowerCase();
        const date = dayjs(row.INVDATE).format("DD/MM/YYYY").toLowerCase();
        return refNo.includes(q) || balAmt.includes(q) || desc.includes(q) || date.includes(q);
      });
  }, [tableLeftList, searchQuery]);

  return (
    <div>
      <div className="w-full flex items-center gap-4">
        <h2 className="text-md text-gray-800 mt-3 mb-1">Invoices</h2>
        <div className="relative mt-1">
          <FaSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input
            type="text"
            placeholder="Search ref, amount, desc..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-7 pr-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 w-48"
          />
        </div>
      </div>
      <div className="h-[30vh] w-full overflow-auto">
        <table className="relative text-xs border-collapse w-full">
          <thead>
            <tr>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Settled
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Select
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Account
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Bal Amt
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Ref No
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Inv Date
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Description
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                FC Bal Amt
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                Job Code
              </th>
              <th className="bg-gray-100 font-normal sticky top-0 border px-2 py-1">
                CUR
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="py-6 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></span>
                    Loading data...
                  </div>
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((row) => {
                const index = row._origIndex;
                return (
                  <tr key={index} className="text-xs">
                    <td className="border bg-white px-2 py-1 text-right">
                      <input
                        type="number"
                        className="w-24 px-2 py-1 border-none rounded text-right text-xs bg-transparent"
                        value={settledAmounts[index] || 0}
                        disabled={!selectedToggle[index]}
                        onChange={(e) => handleSettledChange(index, e.target.value)}
                      />
                    </td>

                    <td className="border w-24 bg-white text-center">
                      <label className="relative inline-block w-[3.5em] h-[2em]">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={!!selectedToggle[index]}
                          onChange={(e) =>
                            handleSelectToggle(index, e.target.checked)
                          }
                        />
                        <span
                          className="
                absolute cursor-pointer inset-0 bg-gray-300 rounded-full transition-all duration-300 ease-in-out
                after:content-[''] after:absolute after:top-[4px] after:left-[4px]
                after:w-[1.5em] after:h-[1.5em] after:bg-white after:rounded-full
                after:transition-all after:duration-300 after:ease-in-out
                after:shadow-[10px_0_40px_rgba(0,0,0,0.1)]
                peer-checked:bg-blue-500
                peer-checked:after:translate-x-[1.5em]
                peer-checked:after:shadow-[-10px_0_40px_rgba(0,0,0,0.1)]
                peer-active:after:w-[3em]
                peer-checked:peer-active:after:translate-x-[0.5em]
              "
                        ></span>
                      </label>
                    </td>

                    <td className="border bg-white px-2 py-1 text-right">
                      {row?.account ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-right">
                      {row?.BalAmt ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1">
                      {row?.INV ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-left cursor-pointer">
                      {row?.INVDATE ? dayjs(row.INVDATE).format("DD/MM/YYYY") : "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-left">
                      {row?.DESCR ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-right">
                      {row?.FCBalAmt ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-left">
                      {row?.job_code ?? "-"}
                    </td>
                    <td className="border bg-white px-2 py-1 text-left">
                      {row?.CUR ?? "-"}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} className="text-center py-4 text-gray-500">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeftTable;
